import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import DatePicker from 'react-datepicker';

import { useLocation } from 'react-router';
import { AutocompleteChips, CustomDropDown, Spinner } from '../../../../shared/components';
import {
  GrievanceForm as GrievanceFormType,
  GrievanceBasicItem,
} from '../../../../services/types/Grievance';
import GrievanceFormContext from './GrievanceFormContext';
import FormDropdown, {
  castToSelectValue,
  handleBooleanChange,
  handleNumericChange,
} from '../../../../shared/components/form/FormDropdown';
import { OperationBasicItem } from '../../../../services/types/Operation';
import useServiceReducer from '../../../../shared/hooks/useServiceReducer';
import operationService from '../../../../services/operationService';
import reducerGrievances, { SearchGrievanceAction } from '../GrievanceSearchReducer';
import grievanceService from '../../../../services/grievanceService';
import reducer, { SearchOperationAction } from '../../../Operations/Operation/Flows/opReducer';
import UIKitButton from '../../../../shared/UIKITComponents/Button';
import UIKitLink from '../../../../shared/UIKITComponents/Link';
import { OptionValue } from '../../../../services/types/Misc';
import Textarea from '../../../../shared/components/Textarea';
import DropdownChecks from '../../../../shared/components/DropdownChecks';
import RouteLeavingGuardGrv from '../../../App/RouteLeavingGuardGrv';
import UIKitModal from '../../../../shared/UIKITComponents/Modal';
import MainContext from '../../../App/MainProvider/context';

interface Props {
  handleSubmit: (values: GrievanceFormType, resetFormCallback: () => void) => void;
  handleCancel: () => void;
  handleDelete: (id: number, grievanceNumber: string) => void;
  setRelatedOperationId: (values: number[]) => void;
  setPrimaryGrievanceId: (value: number[]) => void;
  setPreviousGrievanceId: (value: number[]) => void;
  formState?: GrievanceFormType;
  isSubmitting: boolean;
  isEditMode?: boolean;
}

const MAX_NAME_LENGTH = 50;
const selectBlank = '\u00A0';

export const grievanceFormSchema = Yup.object().shape({
  // 1. Complainant Information
  claimantName: Yup.string().max(MAX_NAME_LENGTH, 'maxCharactersReached').required('required'),
  claimantLastName: Yup.string().max(MAX_NAME_LENGTH, 'maxCharactersReached').required('required'),
  grievanceGenderId: Yup.number().nullable().notRequired(),
  countryClaimantId: Yup.number().required('required').min(1, 'required'),
  emailClaimant: Yup.string()
    .max(254, 'maxCharactersReached')
    .email('Invalid Email')
    .required('required'),

  // 2. Grievance Operation Information provided by Complainant
  phoneClaimant: Yup.string().max(20, 'maxCharactersReached').nullable().notRequired(),
  programOrPrjNumber: Yup.string()
    .max(MAX_NAME_LENGTH, 'maxCharactersReached')
    .nullable()
    .notRequired(),

  programOrPrjName: Yup.string().max(255, 'maxCharactersReached').required('required'),

  programOrPrjCountryId: Yup.number().required('required').min(1, 'required'),

  grievanceDescription: Yup.string().max(750, 'maxCharactersReached').required('required'),
  groupClaimNumber: Yup.number()
    .nullable()
    .notRequired()
    .typeError('Must be a number')
    .integer('Decimals are not allowed')
    .min(0, 'Must be positive')
    .max(9999, 'Group Claim Number must not exceed 4 characters')
    // .transform((value, originalValue) => (originalValue === '' ? null : value))
    .test('no-special-chars', 'Only whole numbers allowed', (val) => {
      if (val === null || val === undefined) {
        return true;
      }
      return /^\d+$/.test(val.toString());
    }),

  receptionOriginPrimaryId: Yup.number().required('required').min(1, 'required'),
  receptionOriginSecondaryId: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable()
    .test(
      'different-origin',
      'Reception Origin (Secondary) must be different from Reception Origin (Primary).',
      function differentOriginTest(value) {
        return !value || value !== this.parent.receptionOriginPrimaryId;
      }
    ),

  receptionChannelPrimaryId: Yup.number().required('required').min(1, 'required'),

  receptionChannelSecondaryId: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable()
    .test(
      'different-channel',
      'Reception Channel (Secondary) must be different from Reception Channel (Primary).',
      function differentChannelTest(value) {
        return !value || value !== this.parent.receptionChannelPrimaryId;
      }
    ),

  contractor: Yup.string().max(100, 'maxCharactersReached').nullable().notRequired(),
  isPGM: Yup.boolean().nullable().notRequired(),
  noPGMReason: Yup.string()
    .max(250, 'maxCharactersReached')
    .nullable()
    .notRequired()
    // then/otherwise concatenados sobre el schema base (misma semántica que
    // Yup 0.x, que resolvía then/otherwise literales con schema.concat(branch)
    // internamente - Fase 7, salto de Yup 0.x a 1.x).
    .when('isPGM', {
      is: false,
      then: (schema) => schema.concat(Yup.string().required('required')),
      otherwise: (schema) => schema.concat(Yup.string().notRequired()),
    }),

  isConfidentialityChange: Yup.boolean().nullable().notRequired(),

  confidentialityChangeReason: Yup.string()
    .max(250, 'maxCharactersReached')
    .nullable()
    .notRequired()
    .when('isConfidentialityChange', {
      is: true,
      then: (schema) => schema.concat(Yup.string().required('required')),
      otherwise: (schema) => schema.concat(Yup.string().notRequired()),
    }),

  confidentialityChangeDate: Yup.date()
    .nullable()
    .when('isConfidentialityChange', {
      is: true,
      then: (schema) =>
        schema.concat(Yup.date().typeError('invalidDate').required('required').nullable()),
      otherwise: (schema) => schema.concat(Yup.date().nullable().notRequired()),
    }),

  mainTopicId: Yup.number().nullable().notRequired(),

  subTopicId: Yup.number().nullable().notRequired(),
  /*
  secondarySubTopicId: Yup.number().when('subTopicId', {
    is: (val: any) => val && Number(val) > 0,
    then: Yup.number().typeError('required').required('required').min(1, 'required'),
    otherwise: Yup.number().nullable().notRequired(),
  }),
*/
  isInitialConfidentiality: Yup.boolean().nullable().required('required'),

  isInminentRisk: Yup.boolean().nullable().notRequired(),
  imminentRiskTypes: Yup.array()
    .of(Yup.number())
    .nullable()
    .when('isInminentRisk', {
      is: true,
      then: (schema) => schema.min(1, 'required').required('required'),
      otherwise: (schema) => schema.notRequired(),
    }),

  imminentRiskTypeOther: Yup.string().max(100, 'maxCharactersReached').nullable().notRequired(),
  operationId: Yup.number().nullable().notRequired(),
  operationCountryCode: Yup.string().nullable().notRequired(),

  isReopenedCase: Yup.boolean()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null) {
        return null;
      }
      return value;
    })
    .notRequired(),

  previousGrievanceId: Yup.number()
    .nullable()
    .when('isReopenedCase', {
      is: true,
      then: (schema) => schema.required('required').moreThan(0, 'required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

  operationCountryId: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('operationId', {
      is: (val: number | null | undefined) => val && Number(val) > 0,
      then: (schema) =>
        schema.concat(Yup.number().required('required').min(1, 'required').typeError('required')),
      otherwise: (schema) => schema.concat(Yup.number().nullable().notRequired()),
    }),
  primaryGrievanceId: Yup.number()
    .nullable()
    .when(['isGroupDialogueProcess', 'isPrimary'], {
      is: (isGroupDialogueProcess: boolean, isPrimary: boolean) =>
        isGroupDialogueProcess === true && isPrimary !== true,
      then: (schema) =>
        schema.concat(Yup.number().required('required').min(1, 'required').typeError('required')),
      otherwise: (schema) => schema.concat(Yup.number().nullable()),
    })
    .test(
      'is-not-already-primary',
      'errorGrievanceIsChild',
      function isNotAlreadyPrimaryTest(value) {
        const { primaryGrievanceHasPrimary } = this.parent;

        if (value && value > 0 && primaryGrievanceHasPrimary === true) {
          return false;
        }
        return true;
      }
    ),
});

const GrievanceForm = (props: Props) => {
  const {
    handleSubmit,
    handleCancel,
    handleDelete,
    setRelatedOperationId,
    setPrimaryGrievanceId,
    setPreviousGrievanceId,
    formState,
    isSubmitting,
    isEditMode,
  } = props;
  const { t } = useTranslation();
  const {
    genders,
    countries,
    countriesClaimant,
    origins,
    channels,
    mainTopics,
    subTopics,
    imminentRiskTypes,
  } = useContext(GrievanceFormContext);

  const { user, inSuperUserMode } = useContext(MainContext);

  const [opRelated, setOpRelated] = useState<OperationBasicItem[]>([]);
  const [primaryGrievances, setPrimaryGrievances] = useState<GrievanceBasicItem[]>([]);
  const [previousGrievances, setPreviousGrievances] = useState<GrievanceBasicItem[]>([]);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // const { history, location } = props;
  const location = useLocation();

  const [grievances, loadingGrievances, , dispatchGrievance] = useServiceReducer(
    grievanceService.getGrievanceList,
    reducerGrievances,
    {
      query: '',
    }
  );

  const [pgrievances, loadingpGrievances, , dispatchpGrievance] = useServiceReducer(
    grievanceService.getGrievanceList,
    reducerGrievances,
    {
      query: '',
    }
  );

  const [operations, loadingOperations, , dispatch] = useServiceReducer(
    operationService.getOperationList,
    reducer,
    {
      query: '',
    }
  );

  const discardChanges = () => {
    setShowDiscardModal(true);
  };

  const DEFAULT_GRIEVANCE: GrievanceFormType = {
    id: 0,
    number: '',
    currentPhaseId: 0,
    isApplicable: null,
    claimantName: '',
    claimantLastName: '',
    grievanceGenderId: 0,
    countryClaimantId: 0,
    emailClaimant: '',
    phoneClaimant: '',
    programOrPrjNumber: '',
    programOrPrjCountryId: 0,
    programOrPrjName: '',
    operationId: null,
    operationCountryCode: '',
    operationCountryId: 0,
    grievanceDescription: '',
    receptionOriginPrimaryId: 0,
    receptionOriginSecondaryId: 0,
    receptionChannelPrimaryId: 0,
    receptionChannelSecondaryId: 0,
    contractor: '',
    isPGM: null,
    noPGMReason: '',
    prioritization: '',
    mainTopicId: 0,
    subTopicId: 0,
    secondarySubTopicId: 0,
    trackType: '',
    isInminentRisk: null,
    imminentRiskTypes: [],
    imminentRiskTypeOther: '',
    isInitialConfidentiality: false,
    isConfidentialityChange: false,
    confidentialityChangeDate: null,
    confidentialityChangeReason: '',
    isGroupClaim: false,
    groupClaimNumber: null,
    isGroupDialogueProcess: false,
    primaryGrievanceId: null,
    primaryGrievanceNumber: '',
    primaryGrievanceHasPrimary: null,
    previousGrievanceId: null,
    previousGrievanceNumber: '',
    isReopenedCase: false,
    operationName: '',
    operationNumber: '',
    notEditable: false,
    isPrimary: false,
    passedApplicability: false,
  };

  const formInit = useMemo(
    () => ({
      ...DEFAULT_GRIEVANCE,
      ...formState,
    }),
    [formState]
  );

  const defaultOption: OptionValue = {
    id: 0,
    text: t('Select'),
    value: '0',
    disabled: false,
  };

  const defaultOptionEmpty: OptionValue = {
    id: 0,
    text: t(''),
    value: '0',
    disabled: false,
  };

  const yesNoRequiredElements: OptionValue[] = [
    { id: 1, text: 'Yes', value: '1' },
    { id: 0, text: 'No', value: '0' },
  ];

  const yesNoElements: OptionValue[] = [
    { id: -1, text: t(''), value: '' },
    { id: 1, text: 'Yes', value: '1' },
    { id: 0, text: 'No', value: '0' },
  ];

  const levelOptions: OptionValue[] = [
    { id: 0, value: '0', text: selectBlank },
    { id: 3, value: '1Low', text: 'Low' },
    { id: 2, value: '2Medium', text: 'Medium' },
    { id: 1, value: '3High', text: 'High' },
  ];
  const trackOptions: OptionValue[] = [
    { id: 0, value: '0', text: selectBlank },
    { id: 1, value: 'Standard Track', text: 'Standard Track' },
    { id: 2, value: 'Fast Track', text: 'Fast Track' },
  ];

  const mapToOptionValue = (
    items: Array<{ id: number; name: string }> | undefined | null,
    defaultOp: boolean = false
  ): OptionValue[] => {
    if (!items || items.length === 0) {
      return [defaultOption];
    }

    const mappedItems = items.map((item) => ({
      id: item.id,
      text: item.name,
      value: String(item.id),
      disabled: false,
    }));

    return [defaultOp ? defaultOption : defaultOptionEmpty, ...mappedItems];
  };

  const mapToOptionRequiredValue = (
    items: Array<{ id: number; name: string }> | undefined | null
  ): OptionValue[] => {
    if (!items || items.length === 0) {
      return [];
    }

    const mappedItems = items.map((item) => ({
      id: item.id,
      text: item.name,
      value: String(item.id),
      disabled: false,
    }));

    return mappedItems;
  };

  const genderOptions: OptionValue[] = useMemo(() => mapToOptionValue(genders, false), [genders]);

  const countryOptions: OptionValue[] = useMemo(
    () => mapToOptionRequiredValue(countries),
    [countries]
  );

  const countryClaimantOptions: OptionValue[] = useMemo(
    () => mapToOptionRequiredValue(countriesClaimant),
    [countriesClaimant]
  );

  const handleOperationsSearch = (query: string) => {
    const action = {
      type: 'QUERY_NUMBER',
      query,
    } as SearchOperationAction;
    dispatch(action);
  };

  const handleGrievancesSearch = (query: string) => {
    const action = {
      type: 'QUERY_NUMBER',
      query,
    } as SearchGrievanceAction;

    dispatchGrievance(action);
  };

  const handlePGrievancesSearch = (query: string) => {
    const action = {
      type: 'QUERY_NUMBER',
      query,
      onlyClosed: true,
    } as SearchGrievanceAction;

    dispatchpGrievance(action);
  };

  const origins1Options: OptionValue[] = useMemo(
    () => mapToOptionRequiredValue(origins),
    [origins]
  );
  const origins2Options: OptionValue[] = useMemo(() => mapToOptionValue(origins), [origins]);
  const channels1Options: OptionValue[] = useMemo(
    () => mapToOptionRequiredValue(channels),
    [channels]
  );
  const channels2Options: OptionValue[] = useMemo(() => mapToOptionValue(channels), [channels]);
  const mainTopicsOptions: OptionValue[] = useMemo(
    () => mapToOptionValue(mainTopics),
    [mainTopics]
  );
  const subTopicsOptions: OptionValue[] = useMemo(() => mapToOptionValue(subTopics), [subTopics]);

  const getSubTopicOptions = (mainTopicId: number): OptionValue[] => {
    if (!subTopics || !mainTopicId) {
      return [defaultOption];
    }

    const mappedItems = subTopics
      .filter((st) => st.mainTopicId === mainTopicId)
      .map((st) => ({
        id: st.id,
        text: st.name,
        value: String(st.id),
      }));
    return [defaultOptionEmpty, ...mappedItems];
  };

  const castBoolToSelectValue = (value: boolean | null | undefined): string => {
    if (value === true) {
      return '1';
    }
    if (value === false) {
      return '0';
    }
    return '';
  };

  const isInitialLoading =
    !genders ||
    genders.length === 0 ||
    !countries ||
    countries.length === 0 ||
    !mainTopics ||
    mainTopics.length === 0 ||
    (isEditMode && formState === undefined);

  return (
    <Formik
      initialValues={formInit}
      validationSchema={grievanceFormSchema}
      enableReinitialize
      onSubmit={(values, { resetForm }) => {
        const payload = { ...values } as GrievanceFormType;
        handleSubmit(payload, resetForm);
      }}
    >
      {(formik) => {
        useEffect(() => {
          if (formik.values.operationId && opRelated.length === 0) {
            const initialOperation = {
              id: formik.values.operationId,
              number: formik.values.operationNumber || formik.values.operationName || 'Selected',
              name: formik.values.operationName || '',
              countryCode: formik.values.operationCountryCode || '',
            };

            setOpRelated([initialOperation]);

            if (setRelatedOperationId) {
              setRelatedOperationId([Number(formik.values.operationId)]);
            }
          }
          if (formik.values.primaryGrievanceId && primaryGrievances.length === 0) {
            const initialPrimaryGrievance = {
              id: formik.values.primaryGrievanceId,
              number: formik.values.primaryGrievanceNumber || '',
              hasPrimary: false,
            };

            setPrimaryGrievances([initialPrimaryGrievance]);

            if (setPrimaryGrievanceId) {
              setPrimaryGrievanceId([Number(formik.values.primaryGrievanceId)]);
            }
          }

          if (formik.values.previousGrievanceId && previousGrievances.length === 0) {
            const initialPreviousGrievance = {
              id: formik.values.previousGrievanceId,
              number: formik.values.previousGrievanceNumber || '',
              hasPrimary: false,
            };

            setPreviousGrievances([initialPreviousGrievance]);

            if (setPreviousGrievanceId) {
              setPreviousGrievanceId([Number(formik.values.previousGrievanceId)]);
            }
          }
        }, [
          formik.values.operationId,
          formik.values.primaryGrievanceId,
          formik.values.previousGrievanceId,
        ]);

        const canDelete =
          (formInit.currentPhaseId === 1 || formInit.currentPhaseId === 2) &&
          (user.grievanceRole === 'GPC' || inSuperUserMode());

        const notEditable = formState ? formState.notEditable : false;

        const [pgKey, setPgKey] = useState(0);
        const [pKey, setPKey] = useState(0);

        if (isInitialLoading) {
          return (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '400px' }}
            >
              <Spinner loading />
            </div>
          );
        }

        const setRationaleSelected = (id: string, checked: boolean) => {
          const numericId = Number(id);

          const currentSelected = [...(formik.values.imminentRiskTypes || [])];

          if (checked) {
            if (!currentSelected.includes(numericId)) {
              currentSelected.push(numericId);
            }
          } else {
            const nextSelected = currentSelected.filter((itemId) => itemId !== numericId);

            formik.setFieldValue('imminentRiskTypes', nextSelected);
            return;
          }

          formik.setFieldValue('imminentRiskTypes', currentSelected);
        };
        const renderError = (
          fieldName: keyof typeof formInit,
          labelName: string,
          maxLength: number
        ) => {
          const error = formik.errors && formik.errors[fieldName];
          const value = formik.values[fieldName] || '';
          const hasBeenSubmitted = formik.submitCount > 0;
          const isTouched = formik.touched[fieldName];

          if ((hasBeenSubmitted || isTouched) && error) {
            return (
              <span className="form__form-group-error">
                {t(error, { field: labelName, count: maxLength })}
              </span>
            );
          }

          if (maxLength && typeof value === 'string' && value.length > maxLength) {
            return (
              <span className="form__form-group-error">
                {t('maxCharactersReached', { field: labelName, count: maxLength })}
              </span>
            );
          }

          return null;
        };

        return (
          <>
            <RouteLeavingGuardGrv
              when={formik.dirty && !isExiting}
              shouldBlockNavigation={() => formik.dirty && !isExiting}
              from={location}
              message={t('sureDiscardChanges')}
            />
            <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
              <UIKitModal
                title={t('discardChanges')}
                size="sm"
                open={showDiscardModal}
                onClose={() => setShowDiscardModal(false)}
                actions={
                  <>
                    <UIKitButton
                      text={t('cancel')}
                      variant="link"
                      onClick={() => setShowDiscardModal(false)}
                      color="secondary"
                    />
                    <UIKitButton
                      text={t('discardChanges')}
                      color="primary"
                      variant="flat"
                      onClick={() => {
                        setIsExiting(true);
                        setTimeout(() => {
                          handleCancel();
                        }, 0);
                      }}
                    />
                  </>
                }
              >
                {t('sureDiscardChanges')}
              </UIKitModal>
              <UIKitModal
                title={t('deleteGrievance')}
                size="sm"
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                actions={
                  <>
                    <UIKitButton
                      text={t('yes')}
                      color="primary"
                      variant="flat"
                      onClick={() => handleDelete(formInit.id, formInit.number)}
                    />
                    <UIKitButton
                      text={t('no')}
                      color="secondary"
                      variant="link"
                      onClick={() => setShowDeleteModal(false)}
                    />
                  </>
                }
              >
                {t('sureDeleteGrievance')}
              </UIKitModal>

              <fieldset disabled={notEditable}>
                <div className="modal__body">
                  <div className="finish-modal modal-dialog--custom-filter">
                    <fieldset disabled={formInit.passedApplicability}>
                      <div className="grievance-headers">1. Complainant Information</div>
                      <hr className="hr-headers" />

                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="claimantName">
                            First Name<span className="error">*</span>
                          </label>
                          <input
                            id="claimantName"
                            placeholder="e.g. Emmett"
                            name="claimantName"
                            type="text"
                            onChange={formik.handleChange}
                            value={formik.values.claimantName}
                            className="custom-filter-form"
                          />
                          {renderError('claimantName', 'First Name', MAX_NAME_LENGTH)}
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="claimantLastName">
                            Last Name<span className="error">*</span>
                          </label>
                          <input
                            id="claimantLastName"
                            name="claimantLastName"
                            placeholder="e.g. Brown"
                            type="text"
                            onChange={formik.handleChange}
                            value={formik.values.claimantLastName}
                            className="custom-filter-form"
                          />
                          {renderError('claimantLastName', 'Last Name', MAX_NAME_LENGTH)}
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="grievanceGenderId">
                            Gender
                          </label>
                          <FormDropdown
                            name="grievanceGenderId"
                            elements={genderOptions}
                            value={castToSelectValue(formik.values.grievanceGenderId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={handleNumericChange(
                              formik,
                              'grievanceGenderId',
                              genderOptions
                            )}
                            disabled={isSubmitting}
                            placeholder={selectBlank}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="countryClaimantId">
                            Country of Residence<span className="error">*</span>
                          </label>
                          <FormDropdown
                            name="countryClaimantId"
                            elements={countryClaimantOptions}
                            value={castToSelectValue(formik.values.countryClaimantId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={handleNumericChange(
                              formik,
                              'countryClaimantId',
                              countryClaimantOptions
                            )}
                            disabled={isSubmitting}
                            placeholder={t('Select')}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="emailClaimant">
                            Email<span className="error">*</span>
                          </label>
                          <input
                            id="emailClaimant"
                            name="emailClaimant"
                            placeholder="e.g. emmettbrown@iadb.org"
                            type="email"
                            onChange={formik.handleChange}
                            value={formik.values.emailClaimant}
                            className="custom-filter-form"
                          />
                          {renderError('emailClaimant', 'Email', 254)}
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="phoneClaimant">
                            Mobile Phone Number
                          </label>
                          <input
                            id="phoneClaimant"
                            name="phoneClaimant"
                            placeholder="e.g. +1 (555) 123-4567"
                            type="text"
                            onChange={formik.handleChange}
                            value={formik.values.phoneClaimant ? formik.values.phoneClaimant : ''}
                            className="custom-filter-form"
                          />
                          {renderError('phoneClaimant', 'Phone', 20)}
                        </div>
                      </div>
                      <div className="grievance-headers mt-4">
                        2. Grievance Operation Information provided by Complainant
                      </div>
                      <hr className="hr-headers" />

                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="programOrPrjNumber">
                            Program or Project Number
                          </label>
                          <input
                            id="programOrPrjNumber"
                            placeholder="e.g. AR-L1010"
                            name="programOrPrjNumber"
                            type="text"
                            onChange={formik.handleChange}
                            value={
                              formik.values.programOrPrjNumber
                                ? formik.values.programOrPrjNumber
                                : ''
                            }
                            className="custom-filter-form"
                          />
                          {renderError(
                            'programOrPrjNumber',
                            'Program or Project Number',
                            MAX_NAME_LENGTH
                          )}
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="programOrPrjCountryId">
                            Program or Project Country<span className="error">*</span>
                          </label>
                          <FormDropdown
                            name="programOrPrjCountryId"
                            elements={countryOptions}
                            value={castToSelectValue(formik.values.programOrPrjCountryId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={handleNumericChange(
                              formik,
                              'programOrPrjCountryId',
                              countryOptions
                            )}
                            disabled={isSubmitting}
                            placeholder={t('Select')}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="col-md-12">
                          <label className="col-form-label" htmlFor="programOrPrjName">
                            Program or Project Name<span className="error">*</span>
                          </label>
                          <input
                            id="programOrPrjName"
                            placeholder="Please indicate program or project name"
                            name="programOrPrjName"
                            type="text"
                            onChange={formik.handleChange}
                            value={
                              formik.values.programOrPrjName ? formik.values.programOrPrjName : ''
                            }
                            className="custom-filter-form"
                          />
                          {renderError('programOrPrjName', 'Program or Project Name', 255)}
                        </div>
                      </div>
                      <div className="grievance-headers mt-4">3. Grievance Overview</div>
                      <hr className="hr-headers" />
                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="operationId">
                            Operation Number
                          </label>
                          <div className="custom-filter-form">
                            <AutocompleteChips<OperationBasicItem>
                              labelKey="number"
                              htmlId="operationId"
                              list={operations}
                              selected={opRelated}
                              placeholder="e.g. AR-L1010"
                              onSearch={handleOperationsSearch}
                              onChange={(selected) => {
                                setOpRelated(selected);
                                const operationId =
                                  selected.length > 0 ? Number(selected[0].id) : 0;
                                const operationName = selected.length > 0 ? selected[0].name : '';
                                const operationNumber =
                                  selected.length > 0 ? selected[0].number : '';
                                const operationCountry =
                                  selected.length > 0
                                    ? countries.find((c) => c.code === selected[0].countryCode)
                                    : '';

                                formik.setFieldValue('operationId', operationId);
                                formik.setFieldValue('operationName', operationName);
                                formik.setFieldValue('operationNumber', operationNumber);
                                formik.setFieldValue(
                                  'operationCountryId',
                                  operationCountry && operationCountry.id !== 27
                                    ? operationCountry.id
                                    : null
                                );
                                formik.setFieldTouched('operationId', true);
                                setRelatedOperationId(selected.map((x) => Number(x.id)));

                                if (!operationCountry) {
                                  formik.setFieldValue('operationCountryId', null);
                                  formik.setFieldTouched('operationCountryId', false);
                                }
                              }}
                              loading={loadingOperations}
                              render={(op: OperationBasicItem) => <div>{op.number}</div>}
                              className="mb-1"
                            />
                          </div>
                        </div>

                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="operationCountryId">
                            Operation Country
                          </label>

                          <FormDropdown
                            name="operationCountryId"
                            elements={countryOptions}
                            value={castToSelectValue(formik.values.operationCountryId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={handleNumericChange(
                              formik,
                              'operationCountryId',
                              countryOptions
                            )}
                            disabled={
                              isSubmitting ||
                              !formik.values.operationNumber ||
                              !formik.values.operationNumber.startsWith('RG-')
                            }
                            placeholder={t('Select')}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="col-md-12" aria-disabled aria-readonly>
                          <label className="col-form-label disabled" htmlFor="operationName">
                            Operation Name
                          </label>
                          <input
                            id="operationName"
                            placeholder="Name of the operation related to the grievance"
                            name="operationName"
                            type="text"
                            disabled
                            onChange={formik.handleChange}
                            value={formik.values.operationName ? formik.values.operationName : ''}
                            className="custom-filter-form"
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="col-md-12">
                          <label className="col-form-label" htmlFor="grievanceDescription">
                            Grievance Description<span className="error">*</span>
                          </label>
                          <Textarea
                            placeholder="Please tell us what happened"
                            name="grievanceDescription"
                            onChange={formik.handleChange}
                            value={formik.values.grievanceDescription}
                          />
                          {renderError('grievanceDescription', 'Grievance Description', 750)}
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="receptionOriginPrimaryId">
                            Reception Origin (Primary)<span className="error">*</span>
                          </label>
                          <FormDropdown
                            name="receptionOriginPrimaryId"
                            elements={origins1Options}
                            value={castToSelectValue(formik.values.receptionOriginPrimaryId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              handleNumericChange(
                                formik,
                                'receptionOriginPrimaryId',
                                origins1Options
                              )(selectedOption);
                              if (!selectedOption || selectedOption === '0') {
                                formik.setFieldValue('receptionOriginSecondaryId', null);
                              }
                            }}
                            disabled={isSubmitting}
                            placeholder={t('select')}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="receptionOriginSecondaryId">
                            Reception Origin (Secondary)
                          </label>
                          <FormDropdown
                            name="receptionOriginSecondaryId"
                            elements={origins2Options}
                            value={castToSelectValue(formik.values.receptionOriginSecondaryId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={handleNumericChange(
                              formik,
                              'receptionOriginSecondaryId',
                              origins2Options
                            )}
                            disabled={isSubmitting || !formik.values.receptionOriginPrimaryId}
                            placeholder={t('select')}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="receptionChannelPrimaryId">
                            Reception Channel (Primary)<span className="error">*</span>
                          </label>
                          <FormDropdown
                            name="receptionChannelPrimaryId"
                            elements={channels1Options}
                            value={castToSelectValue(formik.values.receptionChannelPrimaryId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              handleNumericChange(
                                formik,
                                'receptionChannelPrimaryId',
                                channels1Options
                              )(selectedOption);
                              if (!selectedOption || selectedOption === '0') {
                                formik.setFieldValue('receptionChannelSecondaryId', null);
                              }
                            }}
                            disabled={isSubmitting}
                            placeholder={t('select')}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="receptionChannelSecondaryId">
                            Reception Channel (Secondary)
                          </label>
                          <FormDropdown
                            name="receptionChannelSecondaryId"
                            elements={channels2Options}
                            value={castToSelectValue(formik.values.receptionChannelSecondaryId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={handleNumericChange(
                              formik,
                              'receptionChannelSecondaryId',
                              channels2Options
                            )}
                            disabled={isSubmitting || !formik.values.receptionChannelPrimaryId}
                            placeholder={t('select')}
                          />
                        </div>
                      </div>
                    </fieldset>

                    <fieldset>
                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="contractor">
                            Operation Contractor
                          </label>
                          <input
                            id="contractor"
                            placeholder="Enter contractor name"
                            name="contractor"
                            type="text"
                            onChange={formik.handleChange}
                            value={formik.values.contractor ? formik.values.contractor : ''}
                            className="custom-filter-form"
                          />
                          {renderError('contractor', 'Operation Contractor', 100)}
                        </div>

                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="isPGM">
                            Previous Contact With Grievance Mechanism (PGM)
                          </label>
                          <FormDropdown
                            name="isPGM"
                            elements={yesNoElements}
                            value={castBoolToSelectValue(formik.values.isPGM)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              handleBooleanChange(formik, 'isPGM')(selectedOption);
                              if (!selectedOption || selectedOption === '1') {
                                formik.setFieldValue('noPGMReason', '');
                              }
                            }}
                            disabled={isSubmitting}
                            placeholder={selectBlank}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="noPGMReason">
                            Reason for No PGM
                          </label>
                          <input
                            id="noPGMReason"
                            placeholder="Briefly explain the reason"
                            name="noPGMReason"
                            type="text"
                            onChange={formik.handleChange}
                            value={formik.values.noPGMReason ? formik.values.noPGMReason : ''}
                            className="custom-filter-form"
                            disabled={
                              isSubmitting ||
                              formik.values.isPGM === true ||
                              formik.values.isPGM === null
                            }
                          />
                          {renderError('noPGMReason', 'Reason for No PGM', 250)}
                        </div>

                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="prioritization">
                            Prioritization
                          </label>
                          <FormDropdown
                            name="prioritization"
                            elements={levelOptions}
                            value={castToSelectValue(formik.values.prioritization)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              formik.setFieldValue('prioritization', selectedOption);
                            }}
                            disabled={isSubmitting}
                            placeholder={t('select')}
                            showCheck={false}
                          />
                        </div>
                      </div>
                    </fieldset>

                    <fieldset>
                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="mainTopicId">
                            Main Topic
                          </label>
                          <FormDropdown
                            name="mainTopicId"
                            elements={mainTopicsOptions}
                            value={castToSelectValue(formik.values.mainTopicId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              handleNumericChange(
                                formik,
                                'mainTopicId',
                                mainTopicsOptions
                              )(selectedOption);
                              formik.setFieldValue('subTopicId', 0);
                              formik.setFieldValue('secondarySubTopicId', 0);
                            }}
                            disabled={isSubmitting}
                            placeholder={selectBlank}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="subTopicId">
                            Sub Topic
                          </label>
                          <FormDropdown
                            name="subTopicId"
                            elements={getSubTopicOptions(formik.values.mainTopicId)}
                            value={castToSelectValue(formik.values.subTopicId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              handleNumericChange(
                                formik,
                                'subTopicId',
                                getSubTopicOptions(formik.values.mainTopicId)
                              )(selectedOption);
                              formik.setFieldValue('secondarySubTopicId', 0);
                            }}
                            disabled={isSubmitting || !formik.values.mainTopicId}
                            placeholder={selectBlank}
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="secondarySubTopicId">
                            Secondary Sub Topic
                          </label>
                          <FormDropdown
                            name="secondarySubTopicId"
                            elements={subTopicsOptions}
                            value={castToSelectValue(formik.values.secondarySubTopicId)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={handleNumericChange(
                              formik,
                              'secondarySubTopicId',
                              subTopicsOptions
                            )}
                            disabled={isSubmitting || !formik.values.subTopicId}
                            placeholder={selectBlank}
                          />
                        </div>
                      </div>
                    </fieldset>

                    <fieldset>
                      <div className="grievance-headers mt-4">4. Grievance Management</div>
                      <hr className="hr-headers" />
                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="trackType">
                            Track Type
                          </label>
                          <FormDropdown
                            name="trackType"
                            elements={trackOptions}
                            value={castToSelectValue(formik.values.trackType)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              formik.setFieldValue('trackType', selectedOption);
                            }}
                            disabled={isSubmitting}
                            placeholder={t('select')}
                            showCheck={false}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="isInminentRisk">
                            Imminent Risk
                          </label>
                          <FormDropdown
                            name="isInminentRisk"
                            elements={yesNoElements}
                            value={castBoolToSelectValue(formik.values.isInminentRisk)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              handleBooleanChange(formik, 'isInminentRisk')(selectedOption);
                              if (!selectedOption || selectedOption === '0') {
                                formik.setFieldValue('imminentRiskTypeOther', '');
                                formik.setFieldValue('imminentRiskTypes', []);
                              }
                            }}
                            disabled={isSubmitting}
                            placeholder={selectBlank}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="imminentRiskTypes">
                            Imminent Risk Type
                          </label>

                          <CustomDropDown
                            id="imminentRiskTypes"
                            text={
                              formik.values.imminentRiskTypes &&
                              formik.values.imminentRiskTypes.length > 0
                                ? `${formik.values.imminentRiskTypes.length} ${t('selected')}`
                                : selectBlank
                            }
                            className="custom-filters-dropdown"
                            disabled={isSubmitting || !formik.values.isInminentRisk}
                          >
                            <DropdownChecks
                              elements={imminentRiskTypes.map((x) => ({
                                id: x.id.toString(),
                                name: x.name,
                              }))}
                              selected={(formik.values.imminentRiskTypes || []).map((id) =>
                                id.toString()
                              )}
                              setSelected={(id, checked) => {
                                setRationaleSelected(id, checked);
                              }}
                              translate={false}
                            />
                          </CustomDropDown>
                          {renderError('imminentRiskTypes', 'Imminent Risk Types', 100)}
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="imminentRiskTypeOther">
                            Imminent Risk Type (Other)
                          </label>
                          <input
                            id="imminentRiskTypeOther"
                            placeholder="Enter another imminent risk type, if not listed"
                            name="imminentRiskTypeOther"
                            type="text"
                            onChange={formik.handleChange}
                            value={
                              formik.values.imminentRiskTypeOther
                                ? formik.values.imminentRiskTypeOther
                                : ''
                            }
                            className="custom-filter-form"
                            disabled={isSubmitting || !formik.values.isInminentRisk}
                          />
                          {renderError('imminentRiskTypeOther', 'Imminent Risk Type (Other)', 100)}
                        </div>
                      </div>
                    </fieldset>

                    <fieldset>
                      <div className="form-row">
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="isInitialConfidentiality">
                            Confidentiality<span className="error">*</span>
                          </label>
                          <FormDropdown
                            name="isInitialConfidentiality"
                            elements={yesNoRequiredElements}
                            value={castBoolToSelectValue(formik.values.isInitialConfidentiality)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={handleBooleanChange(formik, 'isInitialConfidentiality')}
                            placeholder={t('select')}
                            disabled={isSubmitting}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="isConfidentialityChange">
                            Confidentiality Change
                          </label>
                          <FormDropdown
                            name="isConfidentialityChange"
                            elements={yesNoRequiredElements}
                            value={castBoolToSelectValue(formik.values.isConfidentialityChange)}
                            className="no-modal dropdown-calculator"
                            onChangeAction={(selectedOption) => {
                              handleBooleanChange(
                                formik,
                                'isConfidentialityChange'
                              )(selectedOption);
                              if (!selectedOption || selectedOption === '0') {
                                formik.setFieldValue('confidentialityChangeDate', null);
                                formik.setFieldValue('confidentialityChangeReason', '');
                              }
                            }}
                            disabled={
                              isSubmitting ||
                              !(formState && formState.id) ||
                              (formState &&
                                formState.id > 0 &&
                                formik.initialValues.isConfidentialityChange === true)
                            }
                            placeholder={t('select')}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="confidentialityChangeDate">
                            Confidentiality Change Date
                          </label>

                          <div className="input-group input-group-date-picker-wrapper">
                            <div className="form__form-group-field form__date-picker-wrapper">
                              <DatePicker
                                id="confidentialityChangeDate"
                                name="confidentialityChangeDate"
                                selected={
                                  formik.values.confidentialityChangeDate
                                    ? new Date(formik.values.confidentialityChangeDate)
                                    : null
                                }
                                onChange={(date: Date | null) =>
                                  formik.setFieldValue('confidentialityChangeDate', date)
                                }
                                dateFormat="dd/MM/yyyy"
                                className="form-control-date-picker grievance-date"
                                autoComplete="off"
                                placeholderText="DD/MM/YYYY"
                                disabled={isSubmitting || !formik.values.isConfidentialityChange}
                              />

                              <div className="form__form-group-icon grievance-date-icon">
                                <span className="material-icons-outlined">calendar_today</span>
                              </div>
                            </div>
                          </div>
                          {renderError(
                            'confidentialityChangeDate',
                            'Confidentiality Change Date',
                            100
                          )}
                        </div>
                        <div className="col-md-3">
                          <label className="col-form-label" htmlFor="confidentialityChangeReason">
                            Confidentiality Change Reason
                          </label>
                          <input
                            id="confidentialityChangeReason"
                            placeholder="Describe the reason for the confidentiality change"
                            name="confidentialityChangeReason"
                            type="text"
                            onChange={formik.handleChange}
                            value={
                              formik.values.confidentialityChangeReason
                                ? formik.values.confidentialityChangeReason
                                : ''
                            }
                            className="custom-filter-form"
                            disabled={isSubmitting || !formik.values.isConfidentialityChange}
                          />
                          {renderError(
                            'confidentialityChangeReason',
                            'Confidentiality Change Reason',
                            250
                          )}
                        </div>
                      </div>
                    </fieldset>
                  </div>

                  <fieldset>
                    <div className="form-row">
                      <div className="col-md-3">
                        <label className="col-form-label" htmlFor="isGroupClaim">
                          Group Claim
                        </label>
                        <FormDropdown
                          name="isGroupClaim"
                          elements={yesNoRequiredElements}
                          value={castBoolToSelectValue(formik.values.isGroupClaim)}
                          className="no-modal dropdown-calculator"
                          onChangeAction={(selectedOption) => {
                            handleBooleanChange(formik, 'isGroupClaim')(selectedOption);
                            if (!selectedOption || selectedOption === '0') {
                              formik.setFieldValue('groupClaimNumber', null);
                            }
                          }}
                          disabled={isSubmitting}
                          placeholder={t('select')}
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="col-form-label" htmlFor="groupClaimNumber">
                          Group Claim Number
                        </label>
                        <input
                          id="groupClaimNumber"
                          placeholder="e.g. 20"
                          name="groupClaimNumber"
                          type="number"
                          onChange={(e) =>
                            formik.setFieldValue(
                              'groupClaimNumber',
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          onKeyDown={(e) => {
                            if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onInput={(e) => {
                            if (e.currentTarget.value.length > 8) {
                              e.currentTarget.value = e.currentTarget.value.slice(0, 8);
                            }
                          }}
                          value={
                            formik.values.groupClaimNumber
                              ? castToSelectValue(formik.values.groupClaimNumber)
                              : ''
                          }
                          className="custom-filter-form"
                          disabled={isSubmitting || !formik.values.isGroupClaim}
                        />
                        {renderError('groupClaimNumber', 'Group Claim Number', 4)}
                      </div>

                      <div className="col-md-3">
                        <label className="col-form-label" htmlFor="isGroupDialogueProcess">
                          Group Dialogue Process
                        </label>
                        <FormDropdown
                          name="isGroupDialogueProcess"
                          elements={yesNoRequiredElements}
                          value={castBoolToSelectValue(formik.values.isGroupDialogueProcess)}
                          className="no-modal dropdown-calculator"
                          onChangeAction={(selectedOption) => {
                            handleBooleanChange(formik, 'isGroupDialogueProcess')(selectedOption);
                            if (!selectedOption || selectedOption === '0') {
                              formik.setFieldValue('primaryGrievanceId', null);
                              setPrimaryGrievances([]);
                              formik.setFieldValue('primaryGrievanceHasPrimary', false);
                              setPgKey((prev) => prev + 1);
                            }
                          }}
                          disabled={isSubmitting || formInit.isPrimary}
                          placeholder={t('select')}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="col-form-label" htmlFor="primaryGrievanceId">
                          Primary Grievance
                        </label>
                        <div className="custom-filter-form">
                          <AutocompleteChips<GrievanceBasicItem>
                            key={pgKey}
                            labelKey="number"
                            htmlId="primaryGrievanceId"
                            list={
                              grievances &&
                              grievances.filter((gr) => gr.number !== formik.values.number)
                            }
                            selected={primaryGrievances}
                            placeholder="e.g. GP-AR-2025-00123"
                            onSearch={handleGrievancesSearch}
                            onChange={async (selected) => {
                              setPrimaryGrievances(selected);

                              const primaryGrievanceNumber =
                                selected.length > 0 ? selected[0].number : '';
                              const primaryGrievanceId =
                                selected.length > 0 ? Number(selected[0].id) : 0;
                              const hasPrimary =
                                selected && selected.length > 0 ? selected[0].hasPrimary : false;
                              await formik.setFieldValue(
                                'primaryGrievanceId',
                                primaryGrievanceId,
                                true
                              );
                              formik.setFieldValue(
                                'primaryGrievanceNumber',
                                primaryGrievanceNumber
                              );

                              await formik.setFieldTouched('primaryGrievanceId', true);
                              formik.setFieldValue('primaryGrievanceHasPrimary', hasPrimary);
                              formik.setFieldTouched('primaryGrievanceId', true, true);
                            }}
                            loading={loadingGrievances}
                            render={(gr: GrievanceBasicItem) => <div>{gr.number}</div>}
                            className="mb-1"
                            disabled={
                              isSubmitting ||
                              !formik.values.isGroupDialogueProcess ||
                              formInit.isPrimary
                            }
                          />
                          {renderError('primaryGrievanceId', t('errorGrievanceIsChild'), 30)}
                        </div>
                      </div>
                    </div>
                  </fieldset>

                  <fieldset>
                    <div className="form-row">
                      <div className="col-md-3">
                        <label className="col-form-label" htmlFor="isReopenedCase">
                          Reopened Case
                        </label>
                        <FormDropdown
                          name="isReopenedCase"
                          elements={yesNoRequiredElements}
                          value={castBoolToSelectValue(formik.values.isReopenedCase)}
                          className="no-modal dropdown-calculator"
                          onChangeAction={(selectedOption) => {
                            handleBooleanChange(formik, 'isReopenedCase')(selectedOption);
                            if (!selectedOption || selectedOption === '0') {
                              formik.setFieldValue('previousGrievanceId', null);
                              formik.setFieldValue('previousGrievanceNumber', '');
                              setPreviousGrievances([]);
                              setPKey((prev) => prev + 1);
                            }
                          }}
                          disabled={isSubmitting}
                          placeholder={t('select')}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="col-form-label" htmlFor="previousGrievanceId">
                          Previous Grievance Number
                        </label>
                        <div className="custom-filter-form">
                          <AutocompleteChips<GrievanceBasicItem>
                            key={pKey}
                            labelKey="number"
                            htmlId="previousGrievanceId"
                            list={pgrievances}
                            selected={previousGrievances}
                            placeholder="e.g. GP-AR-2025-00123"
                            onSearch={handlePGrievancesSearch}
                            onChange={async (selected) => {
                              setPreviousGrievances(selected);

                              const previousGrievanceNumber =
                                selected.length > 0 ? selected[0].number : '';
                              const previousGrievanceId =
                                selected.length > 0 ? Number(selected[0].id) : 0;
                              await formik.setFieldValue(
                                'previousGrievanceId',
                                previousGrievanceId,
                                true
                              );

                              await formik.setFieldTouched('previousGrievanceId', true);
                              formik.setFieldValue(
                                'previousGrievanceNumber',
                                previousGrievanceNumber
                              );
                              formik.setFieldTouched('previousGrievanceId', true, true);
                            }}
                            loading={loadingpGrievances}
                            render={(gr: GrievanceBasicItem) => <div>{gr.number}</div>}
                            className="mb-1"
                            disabled={isSubmitting || !formik.values.isReopenedCase}
                          />
                          {renderError('previousGrievanceId', t('Previous Grievance Number'), 30)}
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </fieldset>
              {/* Botones */}
              <div className="modal__footer modal__foote--custom-filter">
                <hr />
                <Spinner loading={isSubmitting} />
                {canDelete && (
                  <UIKitButton
                    key="btnDelete"
                    variant="flat"
                    color="secondary"
                    text={t('deleteGrievance')}
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => setShowDeleteModal(true)}
                  />
                )}
                <UIKitLink
                  key="btnCancel"
                  className="pt-2"
                  text={t('cancel')}
                  size="large"
                  onClick={formik.dirty ? discardChanges : handleCancel}
                />
                <UIKitButton
                  key="btnSave"
                  text={formState && formState.id ? 'Save' : 'Create Grievance'}
                  type="submit"
                  disabled={isSubmitting || notEditable || !formik.dirty}
                />
              </div>
            </form>
          </>
        );
      }}
    </Formik>
  );
};

export default GrievanceForm;
